import React, { useEffect, useState } from 'react';
import { Trash2 } from 'lucide-react';
import Modal from '../../../../components/Modal';
import { Button, Select } from '../../../../components';
import { InitialsAvatar } from '../shared';
import { useDutyRoster } from '../DutyRosterContext';
import { DeleteTeamConfirm } from './ManageTeamsModal';

const RenameTeamModal = () => {
    const { renameModal, setRenameModal, editManagers, editMembers, editLoading, savingRename, closeRenameTeam, submitRenameTeam } = useDutyRoster();
    const [confirmDelete, setConfirmDelete] = useState(false);
    useEffect(() => { setConfirmDelete(false); }, [renameModal?.id]);
    return (
            <Modal
                isOpen={!!renameModal}
                onClose={closeRenameTeam}
                title="Edit team"
                maxWidth="max-w-[480px]"
            >
                {renameModal && (
                    <div className="space-y-4 pt-1">
                        <div>
                            <label className="block text-[11px] font-extrabold uppercase tracking-wider text-[#475569] dark:text-gray-400 mb-1.5">Team name</label>
                            <input
                                type="text"
                                autoFocus
                                value={renameModal.name}
                                onChange={(e) => setRenameModal((m) => ({ ...m, name: e.target.value }))}
                                onKeyDown={(e) => { if (e.key === 'Enter') submitRenameTeam(); }}
                                placeholder="e.g. Weekend Care Team"
                                className="w-full px-3 py-2.5 rounded-lg border border-[#E2E8F0] dark:border-gray-700 dark:bg-[#31373f] dark:text-gray-200 focus:border-blue-500 outline-none text-[13px]"
                            />
                        </div>

                        <div>
                            <label className="block text-[11px] font-extrabold uppercase tracking-wider text-[#475569] dark:text-gray-400 mb-1.5">Manager</label>
                            <Select
                                className="w-full"
                                value={renameModal.manager_id}
                                onChange={(v) => setRenameModal((m) => ({ ...m, manager_id: v }))}
                                disabled={editLoading}
                                placeholder="No manager"
                                options={[{ value: '', label: 'No manager' }, ...editManagers.map((m) => ({ value: String(m.id), label: m.name }))]}
                            />
                        </div>

                        <div>
                            <label className="block text-[11px] font-extrabold uppercase tracking-wider text-[#475569] dark:text-gray-400 mb-1.5">
                                Team members{!editLoading ? ` (${editMembers.length})` : ''}
                            </label>
                            <div className="rounded-xl border border-[#E2E8F0] dark:border-gray-800 bg-[#FBFCFE] dark:bg-[#20242c] max-h-[200px] overflow-y-auto divide-y divide-[#EDF1F6] dark:divide-gray-800">
                                {editLoading ? (
                                    <div className="py-6 text-center text-[12px] text-[#94A3B8] dark:text-gray-500">Loading…</div>
                                ) : editMembers.length === 0 ? (
                                    <div className="py-6 text-center text-[12px] text-[#94A3B8] dark:text-gray-500">No staff in this team. Use “Manage teams” to add some.</div>
                                ) : (
                                    editMembers.map((s) => (
                                        <div key={s.id} className="flex items-center gap-3 px-3 py-2">
                                            <InitialsAvatar name={s.name} size="md" />
                                            <div className="min-w-0">
                                                <div className="text-[13px] font-bold text-[#1E293B] dark:text-gray-200 truncate">{s.name}</div>
                                                <div className="text-[10px] font-bold uppercase tracking-wide text-[#94A3B8] dark:text-gray-500">
                                                    {s.user_id || '—'}{s.shift_name ? ` · ${s.shift_name}` : ''}
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>

                        {confirmDelete && (
                            <div className="rounded-xl border border-rose-200 dark:border-rose-900/50 overflow-hidden">
                                <DeleteTeamConfirm
                                    team={{ id: renameModal.id, name: renameModal.name || 'this team' }}
                                    staffCount={editMembers.length}
                                    onCancel={() => setConfirmDelete(false)}
                                    onDeleted={() => { setConfirmDelete(false); setRenameModal(null); }}
                                />
                            </div>
                        )}

                        <div className="flex items-center justify-end gap-2.5 pt-1">
                            {!confirmDelete && (
                                <button
                                    type="button"
                                    onClick={() => setConfirmDelete(true)}
                                    disabled={savingRename}
                                    className="mr-auto inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-rose-600 hover:underline disabled:opacity-50"
                                >
                                    <Trash2 className="w-4 h-4" /> Delete team
                                </button>
                            )}
                            <Button
                                type="button"
                                variant="secondary"
                                onClick={closeRenameTeam}
                                disabled={savingRename}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="button"
                                variant="primary"
                                onClick={submitRenameTeam}
                                loading={savingRename}
                            >
                                Save
                            </Button>
                        </div>
                    </div>
                )}
            </Modal>
    );
};

export default RenameTeamModal;
